'use es6';

import PropTypes from 'prop-types';
import { memoize } from 'underscore';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import VerticalEdgeToolbar from 'layout-dnd-components/DndToolbar/VerticalEdgeToolbar';
import { updateSelectedItem } from 'ContentEditorUI/redux/actions/selectionActions';
import { cloneRowFromLayoutSection, requestCloneFromLayoutSection, deleteRowFromLayoutSection } from 'ContentEditorUI/redux/actions/moduleActions';
import { ROW } from 'ContentEditorUI/constants/cloneTypes';
import { logLayoutSectionEvent } from 'InpageEditorUI/eventsAsActions/layoutTransitionEventsAsActions';
import { LAYOUT_SECTION_TRACKING } from 'ContentEditorUI/lib/layoutSection/constants';
import { startGeneratedHublRequest } from 'ContentEditorUI/redux/actions/hublActions';
import { hashFunctionForObject, styledHandleButtonComponent, styledIconButtonComponent } from './helpers';
import ColumnIcon from './ColumnIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const RowIcon = ColumnIcon; // Use column icon for row too since it is rotated

export const buildStyleRowAction = (rowName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'row-styles',
  icon: 'edit',
  iconClassName: 'row-icon',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.editStyles"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.editStyles"
  }),
  onlyInDropdown,
  callback: () => {
    dispatch(updateSelectedItem(rowName, {
      containerId: layoutSectionId
    }));
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.EDIT_LAYOUT_STYLES, {
      type: 'ROW'
    }));
  },
  placement: 'right'
});
export const buildDuplicateRowAction = (rowName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'row-duplicate',
  icon: 'duplicate',
  iconClassName: 'row-icon',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.clone"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.clone"
  }),
  onlyInDropdown,
  callback: () => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.CLONE_ROW));
    dispatch(requestCloneFromLayoutSection(rowName, layoutSectionId, ROW, cloneRowFromLayoutSection(rowName, layoutSectionId)));
  },
  placement: 'right'
});
export const buildDeleteRowAction = (rowName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'row-delete',
  icon: 'delete',
  iconClassName: 'row-icon',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.delete"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.delete"
  }),
  onlyInDropdown,
  callback: () => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.DELETE_ROW));
    dispatch(deleteRowFromLayoutSection(rowName, layoutSectionId));
  },
  placement: 'right'
});
const buildCopyAsHublAction = (rowName, layoutSectionId, dispatch) => ({
  id: 'copy-as-hubl',
  icon: 'customHubl',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  menuTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  onlyInDropdown: true,
  callback: () => {
    dispatch(startGeneratedHublRequest(rowName, layoutSectionId));
  }
});
const buildHideOnMobileAction = (bus, toggleNodeVisibilityOnThisBreakpoint) => ({
  id: 'row-hide',
  icon: 'hide',
  iconInstanceSize: 13,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.hide"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.row.hide"
  }),
  callback: e => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.HIDE_ROW));
    toggleNodeVisibilityOnThisBreakpoint(false, e);
  }
});
export const buildActionsForRow = memoize(({
  rowName,
  layoutSectionId,
  isInDefaultBreakpointMode,
  dispatch,
  bus,
  isInDeveloperMode,
  toggleNodeVisibilityOnThisBreakpoint,
  canToggleRowVisibility
}) => {
  const actions = [buildStyleRowAction(rowName, layoutSectionId, dispatch, bus, {
    onlyInDropdown: false
  })];
  if (isInDefaultBreakpointMode) {
    actions.push(buildDuplicateRowAction(rowName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false
    }), buildDeleteRowAction(rowName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false
    }));
    if (isInDeveloperMode) {
      actions.push(buildCopyAsHublAction(rowName, layoutSectionId, dispatch));
    }
  } else {
    // if row is not in the sidebar, there is no way to unhide it, so we don't want to include the button
    if (canToggleRowVisibility) {
      actions.push(buildHideOnMobileAction(bus, toggleNodeVisibilityOnThisBreakpoint));
    }
  }
  return actions;
}, hashFunctionForObject);
export const BaseResponsiveRowToolbar = ({
  active,
  style,
  actions,
  onHandleMouseDown,
  onDropdownOpenChange,
  responsiveSize,
  connectDragSource,
  isSelectedItem
}) => {
  const buttonActions = [...actions];
  const styleActionIndex = buttonActions.findIndex(action => action.id === 'row-styles');
  const styleActionCallback = buttonActions[styleActionIndex] ? buttonActions[styleActionIndex].callback : () => {};

  // Assume style action button's callback should be used when clicking on the "icon" of this toolbar
  const iconClickHandler = styleActionIndex === -1 || buttonActions[styleActionIndex].disabled ? null : styleActionCallback;
  if (isSelectedItem && styleActionIndex !== -1) {
    buttonActions.splice(styleActionIndex, 1);
  }
  const classes = 'hs-ls-responsive-toolbar hs-ls-responsive-row-toolbar' + (!active ? " hs-ls-toolbar-inactive" : "");
  return /*#__PURE__*/_jsx(VerticalEdgeToolbar, {
    active: active
    // Use column icon for row too since it is rotated
    ,
    icon: RowIcon,
    actions: buttonActions,
    style: style,
    responsiveSize: responsiveSize,
    onHandleMouseDown: onHandleMouseDown,
    onDropdownOpenChange: onDropdownOpenChange,
    onHandleClick: iconClickHandler,
    className: classes,
    handleTooltipPlacement: "right",
    renderWrappedHandle: connectDragSource ? ({
      handleButton
    }) => connectDragSource( /*#__PURE__*/_jsx("div", {
      children: handleButton
    })) : undefined,
    handleTooltip: I18n.text('inpage.actionToolbar.row.label'),
    StyledIconButton: styledIconButtonComponent,
    StyledHandleButton: styledHandleButtonComponent,
    draggable: true
  });
};
BaseResponsiveRowToolbar.propTypes = {
  active: PropTypes.bool.isRequired,
  style: PropTypes.object,
  actions: PropTypes.array.isRequired,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  responsiveSize: PropTypes.string,
  connectDragSource: PropTypes.func,
  isSelectedItem: PropTypes.bool
};